import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Target, Users, Zap } from "lucide-react";
import { Check } from "lucide-react";

const values = [
  {
    icon: Target,
    title: "Innovation",
    description:
      "Continuously improving AI technologies to solve complex document challenges. ",
  },
  {
    icon: Users,
    title: "Customer Impact",
    description:
      "Building solutions that create measurable efficiency and productivity gains",
  },
  {
    icon: Zap,
    title: "Reliability ",
    description:
      "Delivering accurate and dependable document processing at scale.",
  },
  // {
  //   icon: Zap,
  //   title: "Security & Trust  ",
  //   description:
  //     "Ensuring sensitive data is handled with the highest level of protection",
  // },
];

export const About = () => {
  return (
    <div className="pt-16 md:pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="section-padding hero-texture min-h-[70vh] flex items-center">
        <div className="container-width">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1
              className="text-2xl md:text-5xl font-bold tracking-tight leading-[1.1] text-neutral-950 mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              What is Bridge Format AIQ?
            </h1>

            <div className="space-y-4 text-neutral-600 text-sm md:text-base leading-relaxed">
              <p>
                Bridge Format AIQ is an AI-powered document intelligence
                platform that converts unstructured documents into structured,
                usable data.
              </p>

              <p>
                The platform automatically understands documents such as
                reports, forms, invoices, contracts, and records, extracts key
                information, and organizes it into structured formats that can
                be used across business systems.
              </p>

              <p>
                By automating document processing, Bridge Format AIQ helps
                organizations reduce manual work, improve data accuracy, and
                accelerate decision-making.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* key characterstics */}
      <section className="section-padding bg-neutral-50">
        <div className="container-width">
          {/* Heading */}
          <div className="max-w-3xl mb-12">
            <h2
              className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950 mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Key Capabilities
            </h2>

            <p className="text-sm md:text-base text-neutral-600 leading-relaxed">
              Powerful AI-driven features designed to transform how your
              organization processes and utilizes documents.
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "AI Document Understanding",
                desc: "Analyze and interpret complex documents across multiple formats.",
              },
              {
                title: "Intelligent Data Extraction",
                desc: "Automatically capture key information and convert it into structured data.",
              },
              {
                title: "Workflow Automation",
                desc: "Trigger automated workflows using extracted document data.",
              },
              {
                title: "System Integrations",
                desc: "Export structured data to CRMs, ERPs, databases, and analytics tools.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300 feature-card"
              >
                <h3 className="text-sm md:text-base font-semibold text-neutral-950 mb-2">
                  {item.title}
                </h3>
                <p className="text-xs md:text-sm text-neutral-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Mission Section */}
      <section className="section-padding">
        <div className="container-width">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div>
              {/* <p className="label-mono mb-6">Our Mission</p> */}

              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950 mb-6">
                Our Mission
              </h2>

              <div className="space-y-4 text-neutral-600 text-sm md:text-base leading-relaxed">
                <p>
                  Our mission is to simplify how businesses work with documents
                  by turning complex, unstructured information into structured
                  data that powers automated workflows and better
                  decision-making.
                </p>

                <p>
                  We believe organizations should spend less time processing
                  documents and more time using data to drive growth,
                  innovation, and operational efficiency.
                </p>

                {/* <p>
                  We believe that when documents become predictable, operations
                  become scalable.
                </p> */}
              </div>
            </div>

            <div
              className="aspect-[4/3] bg-cover bg-center rounded-lg"
              style={{
                backgroundImage:
                  "url(https://images.pexels.com/photos/7640434/pexels-photo-7640434.jpeg)",
              }}
            />
          </motion.div>
        </div>
      </section>
      {/* Vision Section */}
      <section className="section-padding bg-neutral-50">
        <div className="container-width">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* LEFT CONTENT */}
            <div>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950 mb-6">
                Our Vision
              </h2>

              <div className="space-y-4 text-neutral-600 text-sm md:text-base leading-relaxed">
                <p>
                  We believe the future of work will be powered by intelligent
                  automation.
                </p>

                <p>
                  As businesses generate more data than ever before, the ability
                  to convert unstructured information into structured insights
                  will become critical.
                </p>

                <p>
                  Bridge Format AIQ is committed to building technology that
                  helps organizations unlock the full value of their document
                  data and transform manual workflows into intelligent,
                  automated systems.
                </p>
              </div>
            </div>

            {/* RIGHT VISUAL BLOCK */}
            <div className="bg-secondary text-white border border-neutral-200 rounded-xl p-6 md:p-8 shadow-sm">
              <div className="flex flex-col justify-center h-full">
                <h3 className="text-lg md:text-xl font-semibold  mb-4">
                  Intelligent Future
                </h3>

                <p className="text-sm md:text-base  leading-relaxed">
                  Enabling organizations to move from manual document handling
                  to fully automated, AI-driven workflows that scale with
                  business growth.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Values Section */}
      <section className="bg-neutral-50 pb-20">
        <div className="container-width">
          <div className="text-center mb-12">
            {/* <p className="label-mono mb-6">Our Values</p> */}

            <h2 className="text-3xl text-left md:text-5xl font-semibold tracking-tight text-neutral-950">
              Our Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((value) => (
              <div key={value.title} className="feature-card group">
                <value.icon className="w-6 h-6 text-neutral-950 group-hover:text-primary mb-4" />

                <h3 className="text-lg md:text-xl font-semibold text-neutral-950 mb-3">
                  {value.title}
                </h3>

                <p className="text-sm md:text-base text-neutral-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* What it does */}
      <section className="section-padding">
        <div className="container-width">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* LEFT SIDE */}
            <div className="max-w-xl">
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950 mb-6">
                What Bridge Format AIQ Does?
              </h2>

              <p className="text-neutral-600 text-sm md:text-base leading-relaxed">
                Bridge Format AIQ provides an AI-powered platform designed to
                automate document-heavy workflows.
              </p>

              <p className="text-neutral-600 text-sm md:text-base leading-relaxed mt-4">
                Our technology enables organizations to understand, structure,
                and act on document data efficiently across systems.
              </p>
            </div>

            {/* RIGHT SIDE — CLEAN TICK LIST */}
            <div className="space-y-10">
              {[
                {
                  title: "Understand complex documents",
                  desc: "AI analyzes documents such as reports, contracts, forms, and claims to identify relevant information.",
                },
                {
                  title: "Extract key data",
                  desc: "Important fields are automatically captured and organized into structured formats.",
                },
                {
                  title: "Automate workflows",
                  desc: "Extracted data can trigger workflows across systems such as CRM, ERP, and analytics platforms.",
                },
                {
                  title: "Integrate with business systems",
                  desc: "Structured data flows seamlessly into the tools teams already use.",
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  {/* SIMPLE BLUE TICK */}
                  <Check className="w-5 h-5 text-[#00bdef] mt-1 flex-shrink-0" />

                  {/* CONTENT */}
                  <div className="mb-2">
                    <h3 className="text-sm md:text-base font-semibold text-neutral-950">
                      {item.title}
                    </h3>
                    <p className="text-xs md:text-sm text-neutral-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      {/* Company Section */}
      {/* <section className="section-padding">
        <div className="container-width">
          <div className="max-w-3xl mx-auto text-center">
            <p className="label-mono mb-6">The Company</p>

            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950 mb-6">
              Dynamics Monk
            </h2>

            <p className="text-lg md:text-xl text-neutral-600 leading-relaxed mb-6">
              Dynamics Monk is an enterprise technology company specializing in
              workflow automation, system integration, and AI-powered document
              processing.
            </p>

            <p className="text-sm md:text-base text-neutral-600 leading-relaxed">
              Bridge Format AIQ is the culmination of years of solving document
              standardization challenges for enterprises across industries — now
              available as a standalone platform.
            </p>
          </div>
        </div>
      </section> */}
      {/* Stats Section */}
      {/* <section className="section-padding bg-neutral-950 text-white">
        <div className="container-width">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "10+", label: "Years of Enterprise Experience" },
              { value: "500+", label: "Enterprise Implementations" },
              { value: "50M+", label: "Documents Processed" },
              { value: "99.9%", label: "Platform Uptime" },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl md:text-5xl font-bold mb-2">
                  {stat.value}
                </div>
                <p className="text-xs md:text-sm text-neutral-400">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section> */}
      {/* CTA Section */}
      <section className="pb-10 md:pb-16">
        <div className="container-width">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950 mb-6">
              Start Your Document Automation Journey
            </h2>

            <p className="text-sm md:text-base text-neutral-600 mb-8">
              Discover how Bridge Format AIQ can help your organization
              transform documents into structured data and automated workflows.
            </p>

            <Link to="/contact-us" className="btn-primary gap-2">
              Talk to an Expert
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
